/**
 * 서랍 머리와 구석 버튼 (그리드펄스 DS 에서 가져옴 , 모바일 BottomSheet 는 이 앱에 없어 뺐다).
 * 머리는 늘 같다 , 제목, 한 줄 설명, 필요하면 왼쪽에 뒤로. 모양은 globals.css 의 .drawer 계열이 정한다.
 */
import { useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from "react";

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "onClick"> {
  label: string;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}

export function IconButton({ label, onClick, className, children, ...rest }: IconButtonProps) {
  return (
    <button {...rest} type="button" onClick={onClick} aria-label={label} title={label} className={["icon-btn", className ?? ""].join(" ").trim()}>
      {children}
    </button>
  );
}

export function CloseButton({ onClose, className }: { onClose: () => void; className?: string }) {
  return (
    <IconButton label="닫기" onClick={onClose} className={className}>
      <svg viewBox="0 0 20 20" className="size-icon-action" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
      </svg>
    </IconButton>
  );
}

interface DrawerHeaderProps {
  title: ReactNode;
  description: ReactNode;
  onClose?: () => void;
  onBack?: () => void;
  backLabel?: string;
  className?: string;
  /** 머리 오른쪽에 서는 것(버튼,배지) */
  aside?: ReactNode;
}

export function DrawerHeader({ title, description, onClose, onBack, backLabel = "뒤로", className, aside }: DrawerHeaderProps) {
  return (
    <header className={["drawer-head", className ?? ""].join(" ").trim()}>
      {onBack && (
        <IconButton label={backLabel} onClick={onBack} className="-ml-label">
          <svg viewBox="0 0 20 20" className="size-icon" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path d="M12 4l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconButton>
      )}
      <div className="min-w-0 flex-1">
        <h2 className="drawer-title">{title}</h2>
        <p className="drawer-desc">{description}</p>
      </div>
      {aside}
      {onClose && <CloseButton onClose={onClose} />}
    </header>
  );
}

/** 서랍 껍데기 , 이 앱에서는 오른쪽 결과 패널,용어 도움말 패널이 쓴다 */
export function Drawer({ children, className, ...rest }: { children: ReactNode; className?: string; "aria-label"?: string }) {
  return (
    <aside className={["drawer", className ?? ""].join(" ").trim()} {...rest}>
      {children}
    </aside>
  );
}

/** 네이티브 dialog가 배경 접근 차단, 포커스 이동·복원과 키보드 탐색을 관리한다. */
export function SideDialog({ label, children, onClose }: {
  label: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog?.showModal();
    return () => {
      if (dialog?.open) dialog.close();
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="side-dialog"
      aria-label={label}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
      }}
    >
      {children}
    </dialog>
  );
}
